import React from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { BsPlus, BsTrash } from "react-icons/bs";
import ratingScaleApi from "../api/ratingScaleApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import PerformanceLocalNav from "../components/common/PerformanceLocalNav";
import StatusBadge from "../components/common/StatusBadge";
import { PageSkeleton } from "../components/common/PageState";
import performanceQueryKeys from "../utils/queryKeys";
import { recordId, unwrapDetail } from "../utils/normalize";

const defaultValues = {
  name: "",
  code: "",
  description: "",
  scaleType: "NUMERIC",
  minimumValue: 1,
  maximumValue: 5,
  allowDecimal: false,
  decimalPrecision: 0,
  status: "ACTIVE",
  values: [
    { value: 5, label: "Outstanding", description: "", color: "green", isPassing: true },
    { value: 4, label: "Meets expectations", description: "", color: "blue", isPassing: true },
    { value: 3, label: "Developing", description: "", color: "orange", isPassing: true },
    { value: 2, label: "Needs improvement", description: "", color: "orange", isPassing: false },
    { value: 1, label: "Unsatisfactory", description: "", color: "red", isPassing: false },
  ],
};

const normalizeForm = (scale) => ({
  ...defaultValues,
  ...scale,
  scaleType: scale?.scaleType || scale?.type || defaultValues.scaleType,
  minimumValue: scale?.minimumValue ?? scale?.minRating ?? defaultValues.minimumValue,
  maximumValue: scale?.maximumValue ?? scale?.maxRating ?? defaultValues.maximumValue,
  allowDecimal: scale?.allowDecimal ?? scale?.allowDecimals ?? defaultValues.allowDecimal,
  values: (scale?.values || scale?.ratings || defaultValues.values).map((value, index) => ({
    ...value,
    sequence: value.sequence ?? index + 1,
    isPassing: value.isPassing ?? value.passing ?? true,
  })),
});

const buildRatingScalePayload = (payload) => {
  const values = (payload.values || []).map((value, index) => ({
    value: Number(value.value),
    label: value.label,
    description: value.description || "",
    sequence: value.sequence ?? index + 1,
    color: value.color || "",
    isPassing: value.isPassing ?? value.passing ?? true,
  }));

  return {
    name: payload.name,
    code: payload.code,
    description: payload.description || "",
    scaleType: payload.scaleType || "NUMERIC",
    minimumValue: Number(payload.minimumValue),
    maximumValue: Number(payload.maximumValue),
    minRating: Number(payload.minimumValue),
    maxRating: Number(payload.maximumValue),
    allowDecimal: Boolean(payload.allowDecimal),
    decimalPrecision: Number(payload.decimalPrecision) || 0,
    values,
    levels: values,
    status: payload.status || "ACTIVE",
  };
};

const RatingScaleFormPage = ({ mode = "create", readonly = false }) => {
  const { ratingScaleId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const previewBg = useColorModeValue("gray.50", "gray.900");
  const mutedText = useColorModeValue("gray.600", "gray.400");

  const scaleQuery = useQuery({
    queryKey: performanceQueryKeys.ratingScales.detail(ratingScaleId),
    queryFn: () => ratingScaleApi.getPerformanceRatingScale(ratingScaleId),
    enabled: Boolean(ratingScaleId),
  });

  const form = useForm({ defaultValues });
  const { control, register, handleSubmit, reset, watch } = form;
  const { fields, append, remove } = useFieldArray({ control, name: "values" });
  const values = watch();

  React.useEffect(() => {
    const scale = unwrapDetail(scaleQuery.data);
    if (scale) reset(normalizeForm(scale));
  }, [reset, scaleQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      mode === "edit"
        ? ratingScaleApi.updatePerformanceRatingScale(ratingScaleId, buildRatingScalePayload(payload))
        : ratingScaleApi.createPerformanceRatingScale(buildRatingScalePayload(payload)),
    onSuccess: (response) => {
      const saved = unwrapDetail(response);
      toast({ title: "Rating scale saved", status: "success", duration: 2000 });
      queryClient.invalidateQueries({ queryKey: performanceQueryKeys.ratingScales.all });
      navigate(`/performance/pms-format/rating-scales/${recordId(saved) || ratingScaleId || ""}`);
    },
  });

  return (
    <ModulePageShell
      title={readonly ? "Rating scale details" : mode === "edit" ? "Edit rating scale" : "Create rating scale"}
      description="Configure compact rating values and score mapping."
      actions={
        !readonly ? (
          <Button size="sm" colorScheme="blue" onClick={handleSubmit((payload) => saveMutation.mutate(payload))} isLoading={saveMutation.isPending}>
            Save scale
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => navigate(`/performance/pms-format/rating-scales/${ratingScaleId}/edit`)}>
            Edit
          </Button>
        )
      }
    >
      <PerformanceLocalNav />
      {scaleQuery.isLoading ? (
        <OperationalPanel><PageSkeleton rows={5} /></OperationalPanel>
      ) : (
        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4}>
          <OperationalPanel gridColumn={{ xl: "span 2" }}>
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input size="sm" isReadOnly={readonly} {...register("name", { required: true })} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Code</FormLabel>
                  <Input size="sm" isReadOnly={readonly} {...register("code", { required: true })} />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Input size="sm" isReadOnly={readonly} {...register("description")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Scale type</FormLabel>
                  <Select size="sm" isDisabled={readonly} {...register("scaleType")}>
                    <option value="NUMERIC">Numeric</option>
                    <option value="DESCRIPTIVE">Descriptive</option>
                    <option value="HYBRID">Hybrid</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Minimum value</FormLabel>
                  <Input size="sm" type="number" isReadOnly={readonly} {...register("minimumValue", { valueAsNumber: true, required: true })} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Maximum value</FormLabel>
                  <Input size="sm" type="number" isReadOnly={readonly} {...register("maximumValue", { valueAsNumber: true, required: true })} />
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select size="sm" isDisabled={readonly} {...register("status")}>
                    <option value="ACTIVE">Active</option>
                    <option value="DRAFT">Draft</option>
                    <option value="INACTIVE">Inactive</option>
                  </Select>
                </FormControl>
                <Checkbox isDisabled={readonly} {...register("allowDecimal")}>Allow decimal values</Checkbox>
              </SimpleGrid>
              <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                <Text fontWeight="semibold">Rating values</Text>
                {!readonly ? (
                  <Button size="sm" variant="outline" leftIcon={<BsPlus />} onClick={() => append({ value: "", label: "", description: "", color: "gray", isPassing: false })}>
                    Add rating
                  </Button>
                ) : null}
              </Flex>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Value</Th>
                      <Th>Label</Th>
                      <Th>Description</Th>
                      <Th>Colour</Th>
                      <Th>Passing</Th>
                      {!readonly ? <Th>Actions</Th> : null}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {fields.map((field, index) => (
                      <Tr key={field.id}>
                        <Td><Input size="sm" type="number" isReadOnly={readonly} {...register(`values.${index}.value`, { valueAsNumber: true })} /></Td>
                        <Td><Input size="sm" isReadOnly={readonly} {...register(`values.${index}.label`)} /></Td>
                        <Td><Textarea size="sm" rows={1} isReadOnly={readonly} {...register(`values.${index}.description`)} /></Td>
                        <Td><Input size="sm" isReadOnly={readonly} {...register(`values.${index}.color`)} /></Td>
                        <Td><Checkbox isDisabled={readonly} {...register(`values.${index}.isPassing`)} /></Td>
                        {!readonly ? (
                          <Td>
                            <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<BsTrash />} onClick={() => remove(index)}>
                              Remove
                            </Button>
                          </Td>
                        ) : null}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          </OperationalPanel>

          <OperationalPanel>
            <HStack justify="space-between" mb={3}>
              <Text fontWeight="semibold">Live preview</Text>
              <StatusBadge value={values.status || "DRAFT"} />
            </HStack>
            <VStack align="stretch" spacing={3}>
              {(values.values || []).map((rating, index) => (
                <Box key={`${rating.value}-${index}`} bg={previewBg} borderRadius="md" p={3}>
                  <HStack justify="space-between" align="flex-start">
                    <Box>
                      <Text fontWeight="semibold" fontSize="sm">
                        {rating.color || "Gray"} - {rating.label || "Untitled rating"}
                      </Text>
                      <Text fontSize="xs" color={mutedText}>{rating.description || "No description"}</Text>
                    </Box>
                    <Badge colorScheme={rating.color || "gray"}>{rating.value}</Badge>
                  </HStack>
                  <Text mt={2} fontSize="xs" color={mutedText}>
                    {rating.isPassing ? "Passing" : "Not passing"}
                  </Text>
                </Box>
              ))}
            </VStack>
          </OperationalPanel>
        </SimpleGrid>
      )}
    </ModulePageShell>
  );
};

export default RatingScaleFormPage;
